// ---- the frame loop ----
// Every frame of every game in this house has the same shape: the clock, the
// resize a browser may not have announced, the round's opening retried, the
// camera's pitch easing home — THEN whatever the game moves, its people and
// its field — and then the letters, the flyers, the slot that breathes, the
// render and the ?dbg readout. The two mains each had a copy of all of it,
// comments and all; this is the one copy, and update() is the game's part in
// the middle of it.
//
// It is also the loop's last line of defence. three.js asks for the next frame
// only after this one returns, so an exception anywhere in a frame used to end
// the game: the picture stood still while the taps went on sounding. A brain's
// throw is caught where it happens (jiddo/src/field.ts); anything else is
// logged — once per message, not sixty times a second — shown on the ?dbg
// readout, and the next frame is asked for regardless. A passing error costs
// one frame; a lasting one leaves the picture standing, which is no worse than
// before, and the taps and the tray still answer. frame() is exported bare for
// the ?dbg handle's tick(), so a test still sees the throw.
import {clips,loadClips,speechGen,voiceEnd} from './audio';
import {chips,respread,updateChips,updateFlyers} from './chips';
import {bindHarness,busy,clock,draw,pin,play,run,size,touch,unpin} from './harness';
import {FIELD} from './field';
import {bindInput,pointer} from './input';
import {announceRound,landed,nextRound,rebuildTray,round,tap,wanted} from './round';
import {T,cam,camR,fadeTrees,fitViewport,placeCam,renderer,scene,view} from './scene';
import {markNeed} from './tray';
import {$} from './util';
import {onWater} from './water';

declare global{interface Window{__p?:unknown}}
const dbgEl=$('dbg');
dbgEl.hidden=location.search.indexOf('dbg')<0;
const dbgOn=!dbgEl.hidden;

let pollW=0, pollH=0;
function resize(){
  if(!innerWidth||!innerHeight)return;           // a hidden tab reports 0x0; the poll picks it up when it shows
  fitViewport(innerWidth,innerHeight);
  pollW=innerWidth; pollH=innerHeight;
  respread();
  placeCam(view.yaw,view.el); rebuildTray();
}
addEventListener('resize',resize);

let update:(t:number,dt:number)=>void=()=>{}, count=()=>'';
let last=0, frames=0, fpsT=0, lastErr='';
export function frame(){
  // dt in seconds, capped so a backgrounded tab does not teleport anybody
  const t=performance.now(), dt=Math.min((t-last)/1000,0.05); last=t;
  if(innerWidth!==pollW||innerHeight!==pollH)resize();          // some browsers skip resize events
  announceRound(t);
  // The pitch comes home by itself three seconds after the last finger leaves,
  // and the zoom drifts home after fifteen — long enough that a pinch holds
  // while he looks at what he pinched, short enough that the game he comes
  // back to tomorrow is the one it was built as. The heading is the player's
  // and stays wherever they left it.
  if(!pointer.down&&t-pointer.idleT>3000)view.el+=(0.28-view.el)*(1-Math.exp(-2*dt));
  if(!pointer.down&&t-pointer.idleT>15000)view.zoom+=(1-view.zoom)*(1-Math.exp(-0.5*dt));
  update(t,dt);                                  // the game: where the camera looks, and everyone who moves
  const {need,nudge}=wanted(t);
  updateChips(t,dt,need,nudge);
  updateFlyers(t,i=>landed(i,t));
  markNeed(nudge?round.step:-1);
  fadeTrees(dt);                                 // after the game has put the camera where it goes this frame
  if(draw.on)renderer.render(scene,cam);        // a soak turns it off (harness.ts); nothing that moves is decided after it
  if(dbgOn&&(frames++,t-fpsT>500)){              // read after render: the counters reset each frame
    dbgEl.textContent=(frames*1000/(t-fpsT)).toFixed(0)+' fps  '+renderer.info.render.calls+' calls  '+
      count()+'  '+innerWidth+'x'+innerHeight+' @'+renderer.getPixelRatio()+(lastErr?'\n⚠ '+lastErr:'');
    frames=0; fpsT=t;
  }
}
function safeFrame(){if(busy())return;try{frame();}catch(err){const m=String(err);if(m!==lastErr){lastErr=m;console.error('frame threw',err);if(dbgOn)dbgEl.textContent+='\n⚠ '+m;}}}
// The ?dbg handle: what a script in the console (or a headless run) can read
// and drive — the loop a frame at a time, a tap at a screen point, the round,
// the voice and the camera. The yaw and the zoom are settable, so a sweep of
// camera angles can be scripted. `game` is the rest of it — who is in the
// field and how to drop one more in — and its getters and setters are kept as
// getters and setters, not read once. The names are the one-file game's, so
// old notes hold. Without ?dbg there is no handle at all.
//
// The harness (harness.ts) rides on it: touch, clock, run, play, pin and
// unpin, for a headless run or a console script. `error` is the last throw a
// frame made and the loop survived — the one thing a picture cannot show —
// and `live` is whether the browser's own loop is drawing at all, which a
// hidden pane's is not.
export function exposeDebug(game:object){
  if(!dbgOn)return;
  bindHarness({frame,lastFrame:()=>last});
  const p={get chips(){return chips},get step(){return round.step},get dealt(){return round.dealt},get intro(){return round.intro},
    get owed(){return round.owed},get target(){return round.target},get clips(){return clips},
    get sayGen(){return speechGen()},get voiceEnd(){return voiceEnd()},
    camera:cam,scene,get cam(){return {yaw:view.yaw,el:view.el,R:camR(),zoom:view.zoom,x:T.x,z:T.z}},
    get zoom(){return view.zoom},set zoom(v:number){view.zoom=v;},set yaw(v:number){view.yaw=v;},
    tick:frame,tap:(x:number,y:number)=>tap(x,y,performance.now()),
    get error(){return lastErr},get live(){return performance.now()-last<100},
    get field(){return {x:FIELD.x,zFar:FIELD.zFar,zNear:FIELD.zNear}},onWater,   // where anyone may go, and where nobody but a swimmer should be
    touch,clock,run,play,pin,unpin,draw,size};
  window.__p=Object.defineProperties(p,Object.getOwnPropertyDescriptors(game));
}
// The boot's last step: the input, the first fit, the clips (which wait on the
// first touch), the first round, and the loop. `count` is what the ?dbg readout
// says about the field — his paws, her plates.
export function startLoop(u:(t:number,dt:number)=>void,c:()=>string){
  update=u; count=c;
  bindInput(); resize(); try{loadClips();}catch(err){}
  nextRound(performance.now());
  renderer.setAnimationLoop(safeFrame);
}
